import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Edit, Download, Mail, MessageCircle, CheckCircle, XCircle,
  FileText, Clock, User, Package, Phone, IndianRupee, CreditCard,
  RefreshCw, Trash2, AlertCircle, Loader2,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useBusiness } from '../../contexts/BusinessContext';
import { salesReturnsAPI } from '../../services/part2-api';
import ConfirmModal from '../../components/modals/ConfirmModal';
import { getApiErrorMessage } from '../../utils/apiErrors';

const STATUS_CONFIG = {
  DRAFT:     { bg: 'bg-slate-100',   text: 'text-slate-700',   icon: Clock,       label: 'Draft' },
  CONFIRMED: { bg: 'bg-blue-100',    text: 'text-blue-700',    icon: CheckCircle, label: 'Confirmed' },
  COMPLETED: { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: CheckCircle, label: 'Completed' },
  CANCELLED: { bg: 'bg-red-100',     text: 'text-red-700',     icon: XCircle,     label: 'Cancelled' },
};

const TYPE_CONFIG = {
  credit_note: { label: 'Credit Note', color: 'text-blue-600',    bg: 'bg-blue-50' },
  refund:      { label: 'Refund',      color: 'text-emerald-600', bg: 'bg-emerald-50' },
  exchange:    { label: 'Exchange',    color: 'text-purple-600',  bg: 'bg-purple-50' },
  stock_only:  { label: 'Stock Only',  color: 'text-slate-600',   bg: 'bg-slate-50' },
};

const SalesReturnDetailPage = () => {
  const { businessId, returnId } = useParams();
  const navigate = useNavigate();
  const { activeBusiness } = useBusiness();
  const [ret, setRet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmModal, setConfirmModal] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [refundForm, setRefundForm] = useState({ amount: 0, method: 'CASH', reference: '' });

  const bid = businessId || activeBusiness?.id;
  const businessPath = bid ? `/business/${bid}` : '';

  const fetchReturn = useCallback(async () => {
    if (!returnId) return;
    try {
      setLoading(true);
      const { data } = await salesReturnsAPI.get(returnId);
      setRet(data);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load sales return'));
    } finally {
      setLoading(false);
    }
  }, [returnId]);

  useEffect(() => {
    if (bid && returnId) fetchReturn();
  }, [bid, returnId, fetchReturn]);

  // ─── Field accessors with party_* / real-column preference ────────────────
  const getReturnNo = (r) => r?.return_no || r?.return_number || '';
  const getCustomerName = (r) => r?.customer_name || r?.party_name || '—';
  const getCustomerPhone = (r) => r?.customer_phone || r?.party_phone || '';
  const getCustomerEmail = (r) => r?.customer_email || r?.party_email || '';
  const getCustomerGstin = (r) => r?.customer_gstin || r?.party_gstin || '';
  const getLinkedInvoiceNo = (r) => r?.linked_invoice_no || r?.linked_invoice_number || '';
  const getTotal = (r) => Number(r?.total ?? r?.grand_total ?? 0);
  const getSubtotal = (r) => Number(r?.subtotal ?? 0);
  const getTaxTotal = (r) => Number(r?.tax_total ?? r?.tax_amount ?? 0);
  const getDiscountTotal = (r) => Number(r?.discount_total ?? r?.discount_amount ?? 0);

  // ─── Actions ──────────────────────────────────────────────────────────────

  const handleBack = () => navigate(`${businessPath}/sales-returns`);
  const handleEdit = () => navigate(`${businessPath}/sales-returns/${returnId}/edit`);

  const handleConfirm = () => {
    setConfirmModal({
      title: 'Confirm Sales Return',
      description: `Confirm ${getReturnNo(ret)}? Inventory will be updated for restocked items.`,
      confirmLabel: 'Confirm Return',
      confirmClass: 'bg-emerald-600 hover:bg-emerald-700',
      icon: CheckCircle,
      iconClass: 'text-emerald-600',
      iconBgClass: 'bg-emerald-100',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await salesReturnsAPI.confirm(returnId);
          toast.success('Sales return confirmed');
          fetchReturn();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to confirm return'));
        }
      },
    });
  };

  const handleCancel = () => {
    const willReverseStock = ret.status === 'CONFIRMED' || ret.status === 'COMPLETED';
    setConfirmModal({
      title: 'Cancel Sales Return',
      description: willReverseStock
        ? `Cancel ${getReturnNo(ret)}? Inventory restock adjustments will be reversed.`
        : `Cancel ${getReturnNo(ret)}?`,
      confirmLabel: 'Cancel Return',
      confirmClass: 'bg-red-600 hover:bg-red-700',
      icon: XCircle,
      iconClass: 'text-red-600',
      iconBgClass: 'bg-red-100',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await salesReturnsAPI.cancel(returnId);
          toast.success('Sales return cancelled');
          fetchReturn();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to cancel return'));
        }
      },
    });
  };

  const handleDelete = () => {
    setConfirmModal({
      title: 'Delete Sales Return',
      description: `Delete draft ${getReturnNo(ret)}? This cannot be undone.`,
      confirmLabel: 'Delete',
      confirmClass: 'bg-red-600 hover:bg-red-700',
      icon: Trash2,
      iconClass: 'text-red-600',
      iconBgClass: 'bg-red-100',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await salesReturnsAPI.delete(returnId);
          toast.success('Sales return deleted');
          navigate(`${businessPath}/sales-returns`);
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to delete return'));
        }
      },
    });
  };

  const handleCreateCreditNote = () => {
    setConfirmModal({
      title: 'Create Credit Note',
      description: `Issue a credit note for ${getReturnNo(ret)}? The return will be marked as completed.`,
      confirmLabel: 'Create Credit Note',
      confirmClass: 'bg-blue-600 hover:bg-blue-700',
      icon: CreditCard,
      iconClass: 'text-blue-600',
      iconBgClass: 'bg-blue-100',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          const response = await salesReturnsAPI.createCreditNote(returnId);
          const cn = response.data?.credit_note;
          toast.success(`Credit note ${cn?.credit_note_number || ''} created`);
          fetchReturn();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to create credit note'));
        }
      },
    });
  };

  // Record-refund modal uses the children slot for a form (amount +
  // method + reference). `confirmDisabled` gates on non-zero amount.
  const handleRecordRefund = () => {
    setRefundForm({
      amount: getTotal(ret),
      method: 'CASH',
      reference: '',
    });
    setConfirmModal({
      kind: 'refund',
      title: 'Record Refund',
      description: 'Record a refund against this sales return. The return will be marked as completed.',
      confirmLabel: 'Record Refund',
      confirmClass: 'bg-emerald-600 hover:bg-emerald-700',
      icon: IndianRupee,
      iconClass: 'text-emerald-600',
      iconBgClass: 'bg-emerald-100',
      onConfirm: async (form) => {
        setConfirmModal(null);
        try {
          await salesReturnsAPI.recordRefund(returnId, {
            amount: Number(form.amount),
            method: form.method,
            reference: form.reference || null,
          });
          toast.success('Refund recorded');
          fetchReturn();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to record refund'));
        }
      },
    });
  };

  const handleDownloadPdf = async () => {
    try {
      const response = await salesReturnsAPI.downloadPdf(returnId);
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `SalesReturn_${getReturnNo(ret) || returnId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to download PDF'));
    }
  };

  const handleSendEmail = async () => {
    setSendingEmail(true);
    try {
      const response = await salesReturnsAPI.sendEmail(returnId);
      toast.success(response.data?.message || 'Email queued');
      if (response.data?.note) toast(response.data.note, { icon: 'ℹ️' });
      fetchReturn();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to send email'));
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSendWhatsApp = async () => {
    try {
      const response = await salesReturnsAPI.sendWhatsApp(returnId);
      toast.success(response.data?.message || 'WhatsApp queued');
      if (response.data?.note) toast(response.data.note, { icon: 'ℹ️' });
      fetchReturn();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to send WhatsApp'));
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-[#F8F9FB]">
        <div className="text-center">
          <div className="w-10 h-10 border-[3px] border-slate-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[14px] text-slate-500">Loading sales return...</p>
        </div>
      </div>
    );
  }

  if (!ret) {
    return (
      <div className="h-full flex items-center justify-center bg-[#F8F9FB]">
        <div className="text-center">
          <RefreshCw className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-[16px] text-slate-600 font-medium">Sales Return not found</p>
          <button onClick={handleBack} className="mt-4 text-blue-600 hover:underline text-[14px]">
            ← Back to Sales Returns
          </button>
        </div>
      </div>
    );
  }

  const statusBadge = STATUS_CONFIG[ret.status] || STATUS_CONFIG.DRAFT;
  const StatusIcon = statusBadge.icon;
  const typeBadge = TYPE_CONFIG[ret.return_type] || TYPE_CONFIG.credit_note;

  const isDraft = ret.status === 'DRAFT';
  const isConfirmed = ret.status === 'CONFIRMED';
  const isCompleted = ret.status === 'COMPLETED';
  const isCancelled = ret.status === 'CANCELLED';
  const isCreditNoteType = ret.return_type === 'credit_note';
  const isRefundType = ret.return_type === 'refund';
  const hasCreditNote = !!ret.credit_note_id;

  const items = ret.items || [];
  const activityLog = Array.isArray(ret.activity_log) ? ret.activity_log : [];

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  };
  const formatDateTime = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };
  const formatCurrency = (amount) => `₹${Number(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const isRefundFlow = confirmModal?.kind === 'refund';

  return (
    <>
    <div className="h-full flex flex-col bg-[#F8F9FB] animate-pageFromRight">
      {/* HEADER */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4 min-w-0">
              <button
                onClick={handleBack}
                className="group flex items-center justify-center w-10 h-10 rounded-xl bg-slate-100 hover:bg-slate-200 transition-all duration-200 active:scale-95 flex-shrink-0"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600 group-hover:text-slate-800 transition-colors" />
              </button>
              <div className="min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <h1 className="text-[20px] font-bold text-slate-800 truncate">{getReturnNo(ret)}</h1>
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[12px] font-semibold ${statusBadge.bg} ${statusBadge.text}`}>
                    <StatusIcon className="w-3.5 h-3.5" />
                    {statusBadge.label}
                  </span>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-[12px] font-medium ${typeBadge.bg} ${typeBadge.color}`}>
                    {typeBadge.label}
                  </span>
                </div>
                <p className="text-[12px] text-slate-500 mt-1">{formatDate(ret.return_date)}</p>
              </div>
            </div>

            {/* Header actions */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {isDraft && (
                <>
                  <button onClick={handleEdit}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors text-[13px] font-medium">
                    <Edit className="w-4 h-4" />
                    Edit
                  </button>
                  <button onClick={handleConfirm}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors text-[13px] font-medium shadow-sm">
                    <CheckCircle className="w-4 h-4" />
                    Confirm
                  </button>
                </>
              )}
              {isConfirmed && isCreditNoteType && !hasCreditNote && (
                <button onClick={handleCreateCreditNote}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors text-[13px] font-medium shadow-sm">
                  <CreditCard className="w-4 h-4" />
                  Create Credit Note
                </button>
              )}
              {isConfirmed && isRefundType && !ret.refund_processed && (
                <button onClick={handleRecordRefund}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors text-[13px] font-medium shadow-sm">
                  <IndianRupee className="w-4 h-4" />
                  Record Refund
                </button>
              )}
              {!isDraft && !isCancelled && (
                <>
                  <button onClick={handleSendEmail} title="Send Email" disabled={sendingEmail}
                    className="p-2 rounded-lg bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                    {sendingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                  </button>
                  <button onClick={handleSendWhatsApp} title="Send WhatsApp"
                    className="p-2 rounded-lg bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors">
                    <MessageCircle className="w-4 h-4" />
                  </button>
                </>
              )}
              <button onClick={handleDownloadPdf} title="Download PDF"
                className="p-2 rounded-lg bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors">
                <Download className="w-4 h-4" />
              </button>
              {isDraft && (
                <button onClick={handleDelete} title="Delete"
                  className="p-2 rounded-lg bg-white border border-red-200 text-red-600 hover:bg-red-50 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
              {(isConfirmed || isCompleted) && (
                <button onClick={handleCancel} title="Cancel Return"
                  className="p-2 rounded-lg bg-white border border-red-200 text-red-600 hover:bg-red-50 transition-colors">
                  <XCircle className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* MAIN CONTENT - 12-col grid: 3 left / 6 center / 3 right */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-[1600px] mx-auto px-6 py-5">
          <div className="grid grid-cols-12 gap-5">

            {/* LEFT — 3 cols */}
            <div className="col-span-3 space-y-4">
              {/* Customer Card */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/60">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center">
                    <User className="w-4 h-4 text-blue-600" />
                  </div>
                  <h3 className="text-[12px] font-semibold text-slate-700 uppercase tracking-wide">Customer</h3>
                </div>
                <p className="text-[14px] font-medium text-slate-800 mb-1">{getCustomerName(ret)}</p>
                {getCustomerPhone(ret) && (
                  <p className="text-[12px] text-slate-500 flex items-center gap-1.5 mb-1">
                    <Phone className="w-3 h-3" />
                    {getCustomerPhone(ret)}
                  </p>
                )}
                {getCustomerEmail(ret) && (
                  <p className="text-[12px] text-slate-500 flex items-center gap-1.5 mb-1">
                    <Mail className="w-3 h-3" />
                    {getCustomerEmail(ret)}
                  </p>
                )}
                {getCustomerGstin(ret) && (
                  <p className="text-[11px] text-slate-400 mt-2">GSTIN: {getCustomerGstin(ret)}</p>
                )}
              </div>

              {/* Linked Invoice Card */}
              {getLinkedInvoiceNo(ret) && (
                <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/60">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-7 h-7 rounded-lg bg-emerald-50 flex items-center justify-center">
                      <FileText className="w-4 h-4 text-emerald-600" />
                    </div>
                    <h3 className="text-[12px] font-semibold text-slate-700 uppercase tracking-wide">Linked Invoice</h3>
                  </div>
                  <p className="text-[14px] font-medium text-slate-800">{getLinkedInvoiceNo(ret)}</p>
                </div>
              )}

              {/* Return Info Card */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/60">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center">
                    <AlertCircle className="w-4 h-4 text-slate-600" />
                  </div>
                  <h3 className="text-[12px] font-semibold text-slate-700 uppercase tracking-wide">Return Info</h3>
                </div>
                <div className="space-y-2 text-[12px]">
                  <div className="flex justify-between gap-2">
                    <span className="text-slate-500">Type</span>
                    <span className={`font-medium ${typeBadge.color}`}>{typeBadge.label}</span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="text-slate-500">Reason</span>
                    <span className="text-slate-700 text-right truncate">{ret.reason_code || ret.reason || '—'}</span>
                  </div>
                  {ret.reason_notes && (
                    <p className="text-[11px] text-slate-500 mt-2 pt-2 border-t border-slate-100">
                      {ret.reason_notes}
                    </p>
                  )}
                </div>
              </div>

              {/* Credit Note linkage (if issued) */}
              {hasCreditNote && (
                <div className="bg-blue-50 rounded-xl p-5 shadow-sm border border-blue-200/60">
                  <div className="flex items-center gap-2 mb-2">
                    <CreditCard className="w-4 h-4 text-blue-600" />
                    <h3 className="text-[12px] font-semibold text-blue-700 uppercase tracking-wide">Credit Note Issued</h3>
                  </div>
                  <p className="text-[14px] font-medium text-blue-900">{ret.credit_note_number}</p>
                </div>
              )}
            </div>

            {/* CENTER — 6 cols */}
            <div className="col-span-6 space-y-4">
              {/* Items Card */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/60">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center">
                    <Package className="w-4 h-4 text-blue-600" />
                  </div>
                  <h3 className="text-[13px] font-semibold text-slate-700 uppercase tracking-wide">Items</h3>
                  <span className="text-[12px] text-slate-400">({items.length})</span>
                </div>
                {items.length === 0 ? (
                  <p className="text-[13px] text-slate-400 py-6 text-center">No items on this return</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="px-2 py-2 text-left text-[11px] font-semibold text-slate-500 uppercase">#</th>
                          <th className="px-2 py-2 text-left text-[11px] font-semibold text-slate-500 uppercase">Item</th>
                          <th className="px-2 py-2 text-center text-[11px] font-semibold text-slate-500 uppercase">Qty</th>
                          <th className="px-2 py-2 text-right text-[11px] font-semibold text-slate-500 uppercase">Rate</th>
                          <th className="px-2 py-2 text-right text-[11px] font-semibold text-slate-500 uppercase">Tax %</th>
                          <th className="px-2 py-2 text-right text-[11px] font-semibold text-slate-500 uppercase">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item, idx) => (
                          <tr key={item.id || idx} className="border-b border-slate-100">
                            <td className="px-2 py-2.5 text-[12px] text-slate-500">{idx + 1}</td>
                            <td className="px-2 py-2.5 text-[13px] text-slate-700">
                              <div className="font-medium">{item.item_name}</div>
                              {item.item_sku && <div className="text-[11px] text-slate-400">{item.item_sku}</div>}
                            </td>
                            <td className="px-2 py-2.5 text-[13px] text-center text-slate-700">
                              {Number(item.quantity || 0).toLocaleString('en-IN')}
                            </td>
                            <td className="px-2 py-2.5 text-[13px] text-right text-slate-700">
                              {formatCurrency(item.rate)}
                            </td>
                            <td className="px-2 py-2.5 text-[13px] text-right text-slate-600">
                              {Number(item.tax_rate || 0)}%
                            </td>
                            <td className="px-2 py-2.5 text-[13px] text-right font-medium text-slate-800">
                              {formatCurrency(item.total_amount || item.amount)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Notes */}
              {(ret.notes || ret.terms_conditions) && (
                <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/60">
                  <h3 className="text-[12px] font-semibold text-slate-700 uppercase tracking-wide mb-2">Notes & Terms</h3>
                  {ret.notes && (
                    <div className="mb-3">
                      <p className="text-[11px] text-slate-500 mb-1">Notes</p>
                      <p className="text-[13px] text-slate-700 whitespace-pre-wrap">{ret.notes}</p>
                    </div>
                  )}
                  {ret.terms_conditions && (
                    <div>
                      <p className="text-[11px] text-slate-500 mb-1">Terms & Conditions</p>
                      <p className="text-[13px] text-slate-700 whitespace-pre-wrap">{ret.terms_conditions}</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* RIGHT — 3 cols */}
            <div className="col-span-3 space-y-4">
              {/* Summary */}
              <div className="bg-amber-50 rounded-xl p-5 shadow-sm border border-amber-200/60">
                <h3 className="text-[12px] font-semibold text-slate-700 uppercase tracking-wide mb-3">Summary</h3>
                <div className="space-y-2 text-[13px]">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Subtotal</span>
                    <span className="text-slate-800 font-medium">{formatCurrency(getSubtotal(ret))}</span>
                  </div>
                  {getDiscountTotal(ret) > 0 && (
                    <div className="flex justify-between">
                      <span className="text-slate-600">Discount</span>
                      <span className="text-red-600">− {formatCurrency(getDiscountTotal(ret))}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-slate-600">Tax</span>
                    <span className="text-emerald-600">+ {formatCurrency(getTaxTotal(ret))}</span>
                  </div>
                  <div className="border-t border-amber-200 pt-2 mt-2">
                    <div className="flex justify-between items-center">
                      <span className="text-[14px] font-semibold text-slate-800">Total</span>
                      <span className="text-[18px] font-bold text-emerald-600">{formatCurrency(getTotal(ret))}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Refund Card (if applicable) */}
              {ret.refund_processed && (
                <div className="bg-white rounded-xl p-5 shadow-sm border border-emerald-200/80">
                  <div className="flex items-center gap-2 mb-3">
                    <IndianRupee className="w-4 h-4 text-emerald-600" />
                    <h3 className="text-[12px] font-semibold text-emerald-700 uppercase tracking-wide">Refund Processed</h3>
                  </div>
                  <p className="text-[16px] font-bold text-slate-800 mb-1">{formatCurrency(ret.refund_amount)}</p>
                  <p className="text-[12px] text-slate-500">via {ret.refund_mode || 'CASH'}</p>
                  {ret.refund_reference && (
                    <p className="text-[11px] text-slate-400 mt-1">Ref: {ret.refund_reference}</p>
                  )}
                  {ret.refund_paid_at && (
                    <p className="text-[11px] text-slate-400 mt-1">{formatDateTime(ret.refund_paid_at)}</p>
                  )}
                </div>
              )}

              {/* Activity Timeline */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/60">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-4 h-4 text-slate-500" />
                  <h3 className="text-[12px] font-semibold text-slate-700 uppercase tracking-wide">Activity</h3>
                </div>
                {activityLog.length === 0 ? (
                  <p className="text-[12px] text-slate-400">No activity yet</p>
                ) : (
                  <div className="space-y-3">
                    {activityLog.slice().reverse().slice(0, 10).map((event, idx) => (
                      <div key={idx} className="flex gap-2">
                        <div className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-[12px] font-medium text-slate-700">
                            {(event.action || '').replace(/_/g, ' ')}
                          </p>
                          {event.user_name && (
                            <p className="text-[11px] text-slate-500">by {event.user_name}</p>
                          )}
                          {event.timestamp && (
                            <p className="text-[11px] text-slate-400">{formatDateTime(event.timestamp)}</p>
                          )}
                          {event.note && (
                            <p className="text-[11px] text-slate-500 italic mt-0.5">{event.note}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <ConfirmModal
      isOpen={!!confirmModal}
      title={confirmModal?.title}
      description={confirmModal?.description}
      confirmLabel={confirmModal?.confirmLabel}
      confirmClass={confirmModal?.confirmClass}
      icon={confirmModal?.icon}
      iconClass={confirmModal?.iconClass}
      iconBgClass={confirmModal?.iconBgClass}
      confirmDisabled={isRefundFlow && !(Number(refundForm.amount) > 0)}
      onConfirm={() => confirmModal?.onConfirm?.(isRefundFlow ? refundForm : undefined)}
      onClose={() => setConfirmModal(null)}
    >
      {isRefundFlow && (
        <div className="space-y-3">
          <div>
            <label className="block text-[12px] text-slate-600 mb-1">Amount</label>
            <input
              type="number"
              value={refundForm.amount}
              onChange={(e) => setRefundForm((f) => ({ ...f, amount: e.target.value }))}
              className="w-full px-3 py-2 text-[13px] border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 focus:outline-none"
              min="0"
              step="0.01"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-[12px] text-slate-600 mb-1">Method</label>
            <select
              value={refundForm.method}
              onChange={(e) => setRefundForm((f) => ({ ...f, method: e.target.value }))}
              className="w-full px-3 py-2 text-[13px] border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 focus:outline-none"
            >
              <option value="CASH">Cash</option>
              <option value="UPI">UPI</option>
              <option value="CARD">Card</option>
              <option value="BANK">Bank Transfer</option>
            </select>
          </div>
          <div>
            <label className="block text-[12px] text-slate-600 mb-1">Reference (optional)</label>
            <input
              type="text"
              value={refundForm.reference}
              onChange={(e) => setRefundForm((f) => ({ ...f, reference: e.target.value }))}
              placeholder="Transaction ID, UTR, etc."
              className="w-full px-3 py-2 text-[13px] border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 focus:outline-none"
            />
          </div>
        </div>
      )}
    </ConfirmModal>
    </>
  );
};

export default SalesReturnDetailPage;
